import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  contactFields,
  isPersonMatchTerm,
  type TPersonContact,
  type TPersonContactResponse,
  type TPersonMatch,
  type TExhibitorWithEntries,
} from '@schemas'
import { apiClient, subscribeToPersonAccessDenials } from '@/lib/apiClient'
import { parseApiErrorMessage, parseApiErrorPayload } from '@/lib/api-errors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const labels: Record<(typeof contactFields)[number], string> = {
  name: 'Name',
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  mailingAddress: 'Mailing address',
  mailingCity: 'Mailing city',
  mailingState: 'Mailing state',
  mailingZipCode: 'Postal code',
  displayCity: 'Display city',
  displayState: 'Display state',
}
export function PersonContactReview({
  showId,
  match,
  personId,
  isOnline,
  onClose,
  onUse,
}: {
  showId: string
  match?: TPersonMatch
  personId?: string
  isOnline: boolean
  onClose: () => void
  onUse?: (entrant: TExhibitorWithEntries) => void
}) {
  const qc = useQueryClient()
  const [review, setReview] = useState<TPersonContactResponse | null>(null)
  const [values, setValues] = useState<TPersonContact | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [pending, setPending] = useState(false)
  const [denied, setDenied] = useState(false)
  const [expired, setExpired] = useState(false)
  const [reload, setReload] = useState(0)
  const generation = useRef(0)
  useEffect(
    () =>
      subscribeToPersonAccessDenials(() => {
        generation.current++
        setReview(null)
        setValues(null)
        setError('')
        setNotice('')
        setDenied(true)
      }),
    []
  )
  // A loaded review, with its unsaved edits, stays through a connectivity change.
  const awaitingConnection = !isOnline && !review
  useEffect(() => {
    let active = true
    const current = ++generation.current
    setReview(null)
    setValues(null)
    setError('')
    setNotice('')
    setDenied(false)
    setExpired(false)
    if (awaitingConnection)
      return () => {
        active = false
      }
    const load = match
      ? apiClient.selectShowPerson(showId, match.matchProof)
      : apiClient.getShowPersonContact(showId, personId!)
    void load
      .then(result => {
        if (active && current === generation.current) {
          setReview(result)
          setValues(result.person)
        }
      })
      .catch(error => {
        if (active && current === generation.current) {
          setExpired(parseApiErrorPayload(error)?.code === 'selection_expired')
          setError(parseApiErrorMessage(error))
        }
      })
    return () => {
      active = false
      generation.current++
    }
  }, [showId, match, personId, awaitingConnection, reload])
  const dirty = Boolean(
    review && values && contactFields.some(field => values[field] !== review.person[field])
  )
  function showError(error: unknown) {
    setNotice('')
    setExpired(parseApiErrorPayload(error)?.code === 'selection_expired')
    setError(parseApiErrorMessage(error, 'Unable to review contacts'))
  }
  async function renewSelection() {
    const selectedPerson = review?.person ?? match
    if (!selectedPerson) return
    const current = generation.current
    setPending(true)
    try {
      let fresh: TPersonContactResponse
      if (personId) {
        fresh = await apiClient.getShowPersonContact(showId, personId)
      } else {
        // Matches are capped and need three letters or digits, so try each stored contact in turn.
        const terms = [selectedPerson.email, selectedPerson.phone, selectedPerson.name].flatMap(
          value => {
            const term = value?.slice(0, 100)
            return term && isPersonMatchTerm(term) ? [term] : []
          }
        )
        let freshMatch: TPersonMatch | undefined
        for (const term of terms) {
          const matches = await apiClient.matchShowPeople(showId, term)
          if (current !== generation.current) return
          freshMatch = matches.people.find(person => person.id === selectedPerson.id)
          if (freshMatch) break
        }
        if (!freshMatch)
          throw new Error(
            'This person could not be found again. Close this review and search for them again.'
          )
        fresh = await apiClient.selectShowPerson(showId, freshMatch.matchProof)
      }
      if (current !== generation.current) return
      // Carry only the edits the user made, keeping other fields from the fresh record.
      const edits =
        review && values
          ? Object.fromEntries(
              contactFields
                .filter(field => values[field] !== review.person[field])
                .map(field => [field, values[field]])
            )
          : {}
      setReview(fresh)
      setValues({ ...fresh.person, ...edits })
      setExpired(false)
      setError('')
      setNotice('Selection renewed. Review your corrections before saving.')
    } catch (error) {
      if (current === generation.current) setError(parseApiErrorMessage(error))
    } finally {
      setPending(false)
    }
  }
  async function save() {
    if (!review || !values) return
    const current = generation.current
    setPending(true)
    setError('')
    try {
      const changes = Object.fromEntries(
        contactFields
          .filter(field => values[field] !== review.person[field])
          .map(field => [field, field === 'name' ? values[field] : values[field] || null])
      )
      const result = await apiClient.updateShowPersonContact(showId, review.person.id, {
        changes,
        reviewToken: review.reviewToken,
        selectionProof: review.selectionProof,
      })
      if (current !== generation.current) return
      setReview(result)
      setValues(result.person)
      void qc.invalidateQueries({ queryKey: ['show-person-match', showId] })
      void qc.invalidateQueries({ queryKey: ['exhibitors'] })
    } catch (error) {
      if (current === generation.current) showError(error)
    } finally {
      setPending(false)
    }
  }
  async function linkPerson() {
    if (!review || dirty) return
    const current = generation.current
    setPending(true)
    setError('')
    try {
      const { exhibitor } = await apiClient.createShowEntrant(showId, {
        personId: review.person.id,
        selectionProof: review.selectionProof,
      })
      if (current !== generation.current) return
      void qc.invalidateQueries({ queryKey: ['exhibitors'] })
      onUse?.(exhibitor)
      onClose()
    } catch (error) {
      if (current === generation.current) showError(error)
    } finally {
      setPending(false)
    }
  }
  return (
    <Dialog
      open
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <DialogContent contentClassName="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review contact details</DialogTitle>
          <DialogDescription>
            Changes update the existing person’s shared contact record. Saving contacts does not
            register an entrant.
          </DialogDescription>
        </DialogHeader>
        {!isOnline && <p role="alert">Connect to review or correct contact details.</p>}
        {denied && (
          <p role="alert">
            Your access changed. Sign in or check your show assignment before searching again.
          </p>
        )}
        {error && <p role="alert">{error}</p>}
        {notice && <p role="status">{notice}</p>}
        {expired && !denied && isOnline && (
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => void renewSelection()}
          >
            Renew selection
          </Button>
        )}
        {error && !expired && !denied && isOnline && (
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => setReload(value => value + 1)}
          >
            Reload contact details
          </Button>
        )}
        {!values && !error && !denied && isOnline && <p role="status">Loading contact details…</p>}
        {values && (
          <form
            onSubmit={event => {
              event.preventDefault()
              void save()
            }}
            className="space-y-3"
          >
            {contactFields.map(field => (
              <div key={field}>
                <Label htmlFor={`contact-${field}`}>{labels[field]}</Label>
                <Input
                  id={`contact-${field}`}
                  value={values[field] ?? ''}
                  type={field === 'email' ? 'email' : 'text'}
                  required={field === 'name'}
                  disabled={pending || !isOnline || denied}
                  onChange={event => setValues({ ...values, [field]: event.target.value })}
                />
              </div>
            ))}
            {values.ahstcMember && <p>AHSTC member</p>}
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={!dirty || pending || !isOnline || denied || expired}>
                Save contact details
              </Button>
              {onUse && (
                <Button
                  type="button"
                  disabled={dirty || pending || !isOnline || denied || expired}
                  onClick={() => void linkPerson()}
                >
                  Use this person
                </Button>
              )}
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
            {dirty && onUse && (
              <p>Save or discard your contact changes before using this person.</p>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
